import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  Grid,
  FormGroup,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { Images } from '../../assets';
import { capitalizeWords } from '../../utils/helper';

type ACType = 'DUCTED' | 'VRV/VRF' | 'CHILLER';
type ServiceType = 'Gas Charging' | 'Repair' | 'Installation';

interface ACItemProps {
  ac: { name: ACType; icon: string };
  initialCount: number;
  disabled?: boolean;
  onCountChange: (name: ACType, newCount: number) => void;
}

const acItems: { name: ACType; icon: string }[] = [
  { name: 'DUCTED', icon: Images.ACICONDUCTED },
  { name: 'VRV/VRF', icon: Images.ACICONVRV_VRF },
  { name: 'CHILLER', icon: Images.ACICONTOWER },
];

const services: ServiceType[] = ['Gas Charging', 'Repair', 'Installation'];

const ACItem: React.FC<ACItemProps> = ({
  ac,
  initialCount,
  disabled,
  onCountChange,
}) => {
  const [count, setCount] = useState<number>(initialCount);

  const handleIncrement = () => {
    const newCount = count + 1;
    setCount(newCount);
    onCountChange(ac.name, newCount);
  };

  const handleDecrement = () => {
    if (count > 0) {
      const newCount = count - 1;
      setCount(newCount);
      onCountChange(ac.name, newCount);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
      p={1}
      border={1}
      borderRadius={4}
      bgcolor="#1E1E1E"
      borderColor="rgba(255, 255, 255, 0.12)"
      sx={{ opacity: disabled ? 0.6 : 1 }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="50%"
        width={50}
        height={50}
      >
        <ServiceIcon imageUrl={ac.icon} />
      </Box>
      <Typography flex={1} marginLeft={2} color={disabled ? '#AAA' : 'white'}>
        {capitalizeWords(ac.name)} AC
      </Typography>
      <Box display="flex" alignItems="center">
        <IconButton
          onClick={handleDecrement}
          disabled={count <= 0 || disabled}
          style={{ color: disabled ? '#AAA' : 'white' }}
        >
          <Remove />
        </IconButton>
        <Typography variant="body1" mx={2} color={disabled ? '#AAA' : 'white'}>
          {count}
        </Typography>
        <IconButton
          onClick={handleIncrement}
          disabled={disabled}
          style={{ color: disabled ? '#AAA' : 'white' }}
        >
          <Add />
        </IconButton>
      </Box>
    </Box>
  );
};

const ServiceIcon: React.FC<{ imageUrl: string }> = ({ imageUrl }) => (
  <img
    src={imageUrl}
    alt="Service Icon"
    style={{ width: '40px', height: '40px' }}
  />
);
const CommercialACForm: React.FC<{
  existingDetails: {
    acList: Record<string, number>;
    services: string[];
  };
  onUpdate: (acList: Record<string, number>, services: string[]) => void;
  setCurrDirty: React.Dispatch<React.SetStateAction<any>>;
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
  ) => void;
}> = ({ existingDetails, onUpdate, setCurrDirty, showSnackbar }) => {
  const [selectedServices, setSelectedServices] = useState<ServiceType[]>(() =>
    existingDetails.services.filter((service): service is ServiceType =>
      (['Gas Charging', 'Repair', 'Installation'] as const).includes(
        service as ServiceType,
      ),
    ),
  );

  const [acCounts, setACCounts] = useState<Record<ACType, number>>(
    existingDetails.acList,
  );

  const handleCountChange = (name: ACType, newCount: number) => {
    setACCounts((prev) => ({ ...prev, [name]: newCount }));
  };

  const handleServiceChange = (service: ServiceType) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service],
    );
  };

  const handleSave = () => {
    const cleanedAcCounts = Object.fromEntries(
      Object.entries(acCounts || {}).filter(([_, value]) => value && value > 0),
    );

    const isAcCountsEmpty =
      !cleanedAcCounts || Object.keys(cleanedAcCounts).length === 0;

    const isServicesEmpty = !selectedServices || selectedServices.length === 0;

    if (isAcCountsEmpty && isServicesEmpty) {
      showSnackbar(
        'Please select at least one AC with quantity or one service',
        'error',
      );
      return;
    }

    onUpdate(cleanedAcCounts, selectedServices);
    setCurrDirty(null);
  };
  return (
    <Card sx={{ backgroundColor: '#1C1C1C', p: 3, borderRadius: 2 }}>
      <Grid container spacing={2}>
        {/* AC Items */}
        <Grid item xs={6}>
          {acItems.map((ac, index) => (
            <ACItem
              key={ac.name}
              ac={ac}
              initialCount={acCounts[ac.name] || 0}
              onCountChange={handleCountChange}
            />
          ))}
        </Grid>

        {/* Services Selection */}
        <Grid item xs={6}>
          <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
            Select Services
          </Typography>

          <FormGroup>
            {services.map((service) => (
              <FormControlLabel
                key={service}
                control={
                  <Checkbox
                    checked={selectedServices.includes(service)}
                    onChange={() => handleServiceChange(service)}
                    sx={{
                      color: 'white',
                      '&.Mui-checked': { color: '#d32f2f' },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: 'white', fontSize: 14 }}>
                    {service}
                  </Typography>
                }
                sx={{ mb: 1 }} // Adds spacing between checkboxes
              />
            ))}
          </FormGroup>

          {/* Save Button */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              mt: 1,
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: '#d32f2f',
            }}
            fullWidth
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

export { CommercialACForm };
